import React, { useEffect, useState } from "react";
import { getActivityLogs, getEntityActivityLogs } from "../../API/activityLogs";
import MainTable from "../Datagrid/dataGridTable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, ChipProps } from "@mui/material";
import "./ActivityLogsList.css";

interface ActivityLogFilters {
  entityType?: string;
  [key: string]: any;
}

interface LogAdmin {
  name: string;
  [key: string]: any;
}

interface LogDetails {
  batchName?: string;
  courseName?: string;
  studentName?: string;
  coachName?: string;
  entityName?: string;
  updatedFields?: string[] | Record<string, any>;
  [key: string]: any;
}

interface ActivityLog {
  _id: string;
  action: string;
  entityType: string;
  admin?: LogAdmin;
  details?: LogDetails;
  createdAt: string;
  batch?: { name: string };
  course?: { name: string };
  student?: { name: string };
  coach?: { name: string };
  entityName: string;
  [key: string]: any;
}

interface ActivityLogsListProps {
  entityType?: string;
  entityId?: string;
  showEntityFilter?: boolean;
  title?: string;
}

export default function ActivityLogsList({
  entityType = "",
  entityId = "",
  showEntityFilter = true,
  title = "Activity Logs"
}: ActivityLogsListProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedEntityType, setSelectedEntityType] = useState(entityType || "all");

  useEffect(() => {
    fetchLogs();
  }, [selectedEntityType, entityId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let data: ActivityLog[] = [];
      if (entityId && selectedEntityType && selectedEntityType !== "all") {
        data = await getEntityActivityLogs(selectedEntityType, entityId);
      } else {
        const filters: ActivityLogFilters = {};
        if (selectedEntityType && selectedEntityType !== "all") {
          filters.entityType = selectedEntityType;
        }
        data = await getActivityLogs(filters);
      }
      setLogs(data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter((log: ActivityLog) => {
    const searchMatch =
      search === "" ||
      (log.admin?.name && log.admin.name.toLowerCase().includes(search.toLowerCase())) ||
      (log.entityName && log.entityName.toLowerCase().includes(search.toLowerCase())) ||
      log.action.toLowerCase().includes(search.toLowerCase());

    const actionMatch = actionFilter === "" || log.action === actionFilter;

    let dateMatch = true;
    if (startDate) {
      const startDateObj = new Date(startDate);
      dateMatch = dateMatch && new Date(log.createdAt) >= startDateObj;
    }
    if (endDate) {
      const endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999);
      dateMatch = dateMatch && new Date(log.createdAt) <= endDateObj;
    }

    return searchMatch && actionMatch && dateMatch;
  });

  function getActionChipProps(params: GridRenderCellParams): ChipProps {
    const action = params.value as string;
    let color: "success" | "error" | "info" = "info";
    let label = action;

    switch (action) {
      case "create":
        color = "success";
        label = "Created";
        break;
      case "update":
        color = "info";
        label = "Updated";
        break;
      case "delete":
        color = "error";
        label = "Deleted";
        break;
      default:
        color = "info";
    }

    return { label, color };
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getEntityName = (row: ActivityLog) => {
    if (row.entityType === "batch") {
      return row.details?.batchName || row.batch?.name || "N/A";
    } else if (row.entityType === "course") {
      return row.details?.courseName || row.course?.name || "N/A";
    } else if (row.entityType === "student") {
      return row.details?.studentName || row.student?.name || "N/A";
    } else if (row.entityType === "coach") {
      return row.details?.coachName || row.coach?.name || "N/A";
    }
    return row.entityName || "N/A";
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Timestamp",
      width: 180,
      editable: false,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "action",
      headerName: "Action",
      width: 120,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        const chipProps = getActionChipProps(params);
        return <Chip {...chipProps} />;
      },
    },
    {
      field: "admin",
      headerName: "Admin",
      width: 150,
      editable: false,
      valueGetter: (params) => params.row.admin?.name || "N/A",
    },
    {
      field: "entityType",
      headerName: "Type",
      width: 120,
      editable: false,
      valueFormatter: (params) => {
        const entityType = params.value as string;
        return entityType.charAt(0).toUpperCase() + entityType.slice(1);
      },
    },
    {
      field: "entityName",
      headerName: selectedEntityType === "all" ? "Name" :
        selectedEntityType === "batch" ? "Batch Name" :
          selectedEntityType === "course" ? "Course Name" :
            selectedEntityType === "student" ? "Student Name" :
              selectedEntityType === "coach" ? "Coach Name" : "Name",
      width: 200,
      editable: false,
      valueGetter: (params) => getEntityName(params.row),
    },
    {
      field: "details",
      headerName: "Details",
      width: 300,
      editable: false,
      valueGetter: (params) => {
        const details = params.row.details;
        if (!details) return "No details available";

        if (params.row.action === "update" && details.updatedFields) {
          if (Array.isArray(details.updatedFields)) {
            return `Updated fields: ${details.updatedFields.join(", ")}`;
          } else if (typeof details.updatedFields === "object") {
            return `Updated fields: ${Object.keys(details.updatedFields).join(", ")}`;
          }
        }

        if (params.row.action === "create") {
          return `Created new ${params.row.entityType}`;
        }

        if (params.row.action === "delete") {
          return `Deleted ${params.row.entityType}`;
        }

        return JSON.stringify(details);
      },
    },
  ];

  return (
    <div className="activity-logs-list">
      <div className="logs-header">
        <h3 className="logs-title">{title}</h3>
        <div className="logs-filters">
          {showEntityFilter && (
            <div className="filter-group">
              <label>Type</label>
              <select
                value={selectedEntityType}
                onChange={(e) => setSelectedEntityType(e.target.value)}
              >
                <option value="all">All Logs</option>
                <option value="batch">Batches</option>
                <option value="course">Courses</option>
                <option value="student">Students</option>
                <option value="coach">Coaches</option>
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>Action</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="create">Created</option>
              <option value="update">Updated</option>
              <option value="delete">Deleted</option>
            </select>
          </div>
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="filter-group search-group">
            <label>Search Logs</label>
            <input
              type="search"
              value={search}
              placeholder="Search logs..."
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>
      <MainTable rows={filteredLogs} columns={columns} loading={loading} />
    </div>
  );
}